import React from 'react';
import { CarComparisonData } from '../../types/carComparison';
import { calculateResults } from '../../utils/carCalculations';

interface CostBreakdownTableProps {
  data: CarComparisonData;
  comparisonData?: CarComparisonData;
  formatCurrency: (value: number) => string;
  scenario: 'buy' | 'finance' | 'lease';
}

const yearlyDepreciationPercent = (years: number): number[] => {
  const totals: number[] = []; 
  let total = 0;
  
  for (let i = 0; i < years; i++) {
    if (i === 0) totals[i] = 1;
    else totals[i] = totals[i - 1] * 4/3;
    total += totals[i];
  }
  
  const results: number[] = [];
  if (total > 0) {
    for (let i = years; i > 0; i--) {
      results[years - i] = totals[i - 1] / total;
    }
  }
  
  return results;
}


const CostBreakdownTable: React.FC<CostBreakdownTableProps> = ({
  data,
  comparisonData,
  formatCurrency,
  scenario
}) => {
  // Helper function to format currency with color (all costs are expenses = RED)
  const formatCurrencyWithColor = (value: number, colorType: 'positive' | 'negative' | 'neutral' = 'neutral') => {
    const formatted = formatCurrency(value);
    const colors = {
      positive: '#059669',  // Green for income/gains
      negative: '#dc2626',  // Red for expenses/losses
      neutral: 'inherit'    // Default text color
    };
    return <span style={{ color: colors[colorType] }}>{formatted}</span>;
  };

  const results = calculateResults(data);
  const comparisonResults = comparisonData ? calculateResults(comparisonData) : null;
  const isComparison = !!comparisonData;

  // Get yearly data for the selected scenario
  const yearlyData = results[scenario].yearlyData;
  const comparisonYearlyData = comparisonResults ? comparisonResults[scenario].yearlyData : null;

  // Calculate totals for each cost category
  const calculateCategoryTotals = (yearlyDataArray: any[]) => {
    const totals = {
      depreciation: 0,
      fuel: 0,
      insurance: 0,
      maintenance: 0,
      financing: 0,
      otherCarCosts: 0,
      opportunityCosts: 0,
      total: 0
    };

    yearlyDataArray.forEach((year: any, index: number) => {
      const totalDepreciation = - yearlyDataArray[0].cashPurchase - yearlyDataArray[yearlyDataArray.length - 1].sellCar;
      // Depreciation (first year includes purchase, last year includes sale)
      totals.depreciation += totalDepreciation * yearlyDepreciationPercent(yearlyDataArray.length)[index];

      // Fuel
      totals.fuel += Math.abs(year.gas || 0);

      // Insurance
      totals.insurance += Math.abs(year.insurance || 0);

      // Maintenance
      totals.maintenance += Math.abs(year.maintenance || 0);

      // Financing (monthly payments + interest)
      totals.financing += Math.abs(year.monthlyCosts || 0);

      // Other car costs (registration, parking, etc.)
      totals.otherCarCosts += Math.abs(year.otherCosts || 0);

      // Opportunity costs (investment returns foregone)
      totals.opportunityCosts += Math.abs(year.investmentReturn || 0);
    });

    totals.total = totals.depreciation + totals.fuel + totals.insurance +
                   totals.maintenance + totals.financing + totals.otherCarCosts + totals.opportunityCosts;

    return totals;
  };


  // Calculate year-by-year costs for each category
  const calculateYearlyCosts = (yearlyDataArray: any[]) => {
    return yearlyDataArray.map((year: any, index: number) => {
      let depreciation = 0;
      const totalDepreciation = -yearlyDataArray[0].cashPurchase - yearlyDataArray[yearlyDataArray.length - 1].sellCar;
      // Depreciation (first year includes purchase, last year includes sale)
      depreciation = totalDepreciation * yearlyDepreciationPercent(yearlyDataArray.length)[index];

      const fuel = Math.abs(year.gas || 0);
      const insurance = Math.abs(year.insurance || 0);
      const maintenance = Math.abs(year.maintenance || 0);
      const financing = Math.abs(year.monthlyCosts || 0);
      const otherCarCosts = Math.abs(year.otherCosts || 0);
      const opportunityCosts = Math.abs(year.investmentReturn || 0);

      return {
        year: year.year,
        depreciation,
        fuel,
        insurance,
        maintenance,
        financing,
        otherCarCosts,
        opportunityCosts,
        total: depreciation + fuel + insurance + maintenance + financing + otherCarCosts + opportunityCosts
      };
    });
  };

  const carAYearlyCosts = calculateYearlyCosts(yearlyData);
  const carATotals = calculateCategoryTotals(yearlyData);

  const carBYearlyCosts = comparisonYearlyData ? calculateYearlyCosts(comparisonYearlyData) : null;
  const carBTotals = comparisonYearlyData ? calculateCategoryTotals(comparisonYearlyData) : null;

  const scenarioLabel = scenario === 'buy' ? 'Buy' : scenario === 'finance' ? 'Finance' : 'Lease';

  return (
    <div style={{ marginTop: '2rem' }}>
      <h3 style={{ marginBottom: '1rem' }}>
        {scenarioLabel} Scenario: {data.ownershipTimeframe}-Year Cost Breakdown
      </h3>

      <div style={{
        overflowX: 'auto',
        border: '1px solid var(--border)',
        borderRadius: '8px'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem'
        }}>
          <thead>
            <tr style={{ background: 'var(--card-background)', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Category</th>
              {carAYearlyCosts.map(yearData => (
                <th key={yearData.year} style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold' }}>
                  Year {yearData.year}
                </th>
              ))}
              <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold', background: '#f0f9ff' }}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Depreciation */}
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '0.75rem', fontWeight: '500' }}>Depreciation</td>
              {carAYearlyCosts.map(yearData => (
                <td key={yearData.year} style={{ padding: '0.75rem', textAlign: 'right' }}>
                  {formatCurrencyWithColor(yearData.depreciation, 'negative')}
                </td>
              ))}
              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', background: '#f0f9ff' }}>
                {formatCurrencyWithColor(carATotals.depreciation, 'negative')}
              </td>
            </tr>

            {/* Fuel */}
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '0.75rem', fontWeight: '500' }}>Fuel</td>
              {carAYearlyCosts.map(yearData => (
                <td key={yearData.year} style={{ padding: '0.75rem', textAlign: 'right' }}>
                  {formatCurrencyWithColor(yearData.fuel, 'negative')}
                </td>
              ))}
              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', background: '#f0f9ff' }}>
                {formatCurrencyWithColor(carATotals.fuel, 'negative')}
              </td>
            </tr>

            {/* Insurance */}
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '0.75rem', fontWeight: '500' }}>Insurance</td>
              {carAYearlyCosts.map(yearData => (
                <td key={yearData.year} style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>
                  {formatCurrency(yearData.insurance)}
                </td>
              ))}
              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', background: '#f0f9ff', color: '#dc2626' }}>
                {formatCurrency(carATotals.insurance)}
              </td>
            </tr>

            {/* Maintenance */}
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '0.75rem', fontWeight: '500' }}>Maintenance</td>
              {carAYearlyCosts.map(yearData => (
                <td key={yearData.year} style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>
                  {formatCurrency(yearData.maintenance)}
                </td>
              ))}
              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', background: '#f0f9ff', color: '#dc2626' }}>
                {formatCurrency(carATotals.maintenance)}
              </td>
            </tr>

            {/* Other Car Costs */}
            {carATotals.otherCarCosts > 0 && (
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.75rem', fontWeight: '500' }}>Other Costs<br/>(Registration, Parking, etc.)</td>
                {carAYearlyCosts.map(yearData => (
                  <td key={yearData.year} style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>
                    {formatCurrency(yearData.otherCarCosts)}
                  </td>
                ))}
                <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', background: '#f0f9ff', color: '#dc2626' }}>
                  {formatCurrency(carATotals.otherCarCosts)}
                </td>
              </tr>
            )}

            {/* Financing */}
            {scenario !== 'buy' && (
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.75rem', fontWeight: '500' }}>
                  {scenario === 'finance' ? 'Financing' : 'Lease Payments'}
                </td>
                {carAYearlyCosts.map(yearData => (
                  <td key={yearData.year} style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>
                    {formatCurrency(yearData.financing)}
                  </td>
                ))}
                <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', background: '#f0f9ff', color: '#dc2626' }}>
                  {formatCurrency(carATotals.financing)}
                </td>
              </tr>
            )}

            {/* Opportunity Costs */}
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '0.75rem', fontWeight: '500' }}>Opportunity Costs<br/>(Investments)</td>
              {carAYearlyCosts.map(yearData => (
                <td key={yearData.year} style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>
                  {formatCurrency(yearData.opportunityCosts)}
                </td>
              ))}
              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', background: '#f0f9ff', color: '#dc2626' }}>
                {formatCurrency(carATotals.opportunityCosts)}
              </td>
            </tr>

            {/* Total Row */}
            <tr style={{
              background: 'var(--card-background)',
              borderTop: '2px solid var(--border)',
              fontWeight: 'bold'
            }}>
              <td style={{ padding: '1rem', fontSize: '1rem' }}>Total Cost of Ownership</td>
              {carAYearlyCosts.map(yearData => (
                <td key={yearData.year} style={{ padding: '1rem', textAlign: 'right', fontSize: '1rem', color: '#dc2626' }}>
                  {formatCurrency(yearData.total)}
                </td>
              ))}
              <td style={{ padding: '1rem', textAlign: 'right', fontSize: '1.1rem', background: '#dbeafe', color: '#dc2626' }}>
                {formatCurrency(carATotals.total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        background: '#f8fafc',
        borderRadius: '8px',
        fontSize: '0.85rem',
        color: '#64748b'
      }}>
        <strong>Note:</strong> Based on {data.ownershipTimeframe}-year ownership with {data.annualMileage.toLocaleString()} km driven per year.
        This table shows actual cash flows including depreciation (purchase price + taxes + fees - resale value).
      </div>
    </div>
  );
};

export default CostBreakdownTable;
